import { useCallback, useEffect, useId, useRef, useState, type FormEvent } from 'react'
import { Button } from '@/components/ui/Button'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { EmptyState, ErrorState, LoadingState, Notice } from '@/components/ui/Feedback'
import { NativeSelect } from '@/components/ui/NativeSelect'
import { SearchField } from '@/components/ui/SearchField'
import { formatDay, formatMoney, todayIso } from '@/lib/format'
import type { AdminFetch } from './AdminCustomers'
import { pesosToCents, stateTone } from './adminLicenseText'

type Status = 'PENDING' | 'VALIDATED' | 'REJECTED'
type Filter = Status | 'ALL'
export type SwingInstallation = {
  id: string; businessName: string; contactName: string; phone: string; email: string; city?: string | null; state?: string | null
  declaredTerminals: number; programVersion?: string | null; hostname?: string | null; os?: string | null; status: Status
  expiresOn?: string | null; serverPriceCents?: number | null; terminals?: number | null; terminalPriceCents?: number | null; annualCents?: number | null
  notes?: string | null; companyId?: string | null; companyName?: string | null; identifiedAt: string; lastSeenAt?: string | null; reviewedAt?: string | null
}
type SwingPayment = { id: string; paidOn: string; reference?: string | null; amountCents: number; previousExpiresOn: string; newExpiresOn: string; recordedAt: string }
type SwingDetail = { installation: SwingInstallation; payments: SwingPayment[] }
type SwingPage = { items: SwingInstallation[]; page: number; hasMore: boolean }
type Review = { status: Status; expiresOn: string; server: string; terminals: string; terminalPrice: string; notes: string; businessName: string; contactName: string; phone: string; email: string }
type Field = 'expiresOn' | 'server' | 'terminals' | 'terminalPrice' | 'email'
type Payment = { paidOn: string; reference: string; amount: string }

const filterLabels: Record<Filter, string> = { PENDING: 'Por validar', VALIDATED: 'Validadas', REJECTED: 'Rechazadas', ALL: 'Todas' }
const statusLabels: Record<Status, string> = { PENDING: 'Por validar', VALIDATED: 'Validada', REJECTED: 'Rechazada' }
const when = new Intl.DateTimeFormat('es-MX', { dateStyle: 'medium', timeStyle: 'short' })

function centsToPesos(cents: number | null | undefined) {
  return cents == null ? '' : (cents / 100).toFixed(2)
}

function reviewOf(installation: SwingInstallation): Review {
  return {
    status: installation.status, expiresOn: installation.expiresOn ?? '', server: centsToPesos(installation.serverPriceCents),
    terminals: String(installation.terminals ?? installation.declaredTerminals), terminalPrice: centsToPesos(installation.terminalPriceCents),
    notes: installation.notes ?? '', businessName: installation.businessName, contactName: installation.contactName, phone: installation.phone, email: installation.email,
  }
}

/** What the review adds up to in a year, or null while a figure is missing or wrong. */
function annualOf(review: Review) {
  const server = pesosToCents(review.server)
  const each = pesosToCents(review.terminalPrice)
  const terminals = review.terminals.trim() === '' ? Number.NaN : Number(review.terminals)
  if (server === null || each === null || Number.isNaN(server) || Number.isNaN(each) || !Number.isInteger(terminals) || terminals < 0) return null
  return server + terminals * each
}

/** The first thing the server would refuse, so the field gets the focus before the request leaves. */
function reviewProblem(review: Review): { field: Field; message: string } | null {
  const server = pesosToCents(review.server)
  const each = pesosToCents(review.terminalPrice)
  const terminals = Number(review.terminals)
  if (Number.isNaN(server)) return { field: 'server', message: 'Escribe el pago anual del servidor en pesos, por ejemplo 3,890.00.' }
  if (review.terminals.trim() !== '' && (!Number.isInteger(terminals) || terminals < 0 || terminals > 1000)) return { field: 'terminals', message: 'Indica cuántas terminales paga, de 0 a 1000.' }
  if (Number.isNaN(each)) return { field: 'terminalPrice', message: 'Escribe el pago anual por terminal en pesos, por ejemplo 500.00.' }
  if (review.status === 'VALIDATED') {
    if (!review.expiresOn) return { field: 'expiresOn', message: 'Indica la fecha en que vence la licencia.' }
    if (server === null) return { field: 'server', message: 'Indica el pago anual del servidor.' }
    if (review.terminals.trim() === '') return { field: 'terminals', message: 'Indica cuántas terminales paga, de 0 a 1000.' }
    if (each === null) return { field: 'terminalPrice', message: 'Indica el pago anual por terminal.' }
  }
  if (review.email.trim() && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(review.email.trim())) return { field: 'email', message: 'Escribe un correo válido.' }
  return null
}

/**
 * The servers of the desktop program BsPOS Swing that identified themselves: the ones to validate first, the
 * licence the admin corroborated, its yearly payments. `selected` is the server whose detail is open, which the
 * licence list can also open; a new `openRequest` reads it again, as when the list asks for the one already open.
 */
export function AdminSwing({ adminFetch, describeError, selected, openRequest = 0, onSelect, onChanged }: {
  adminFetch: AdminFetch; describeError: (cause: unknown, fallback: string) => string
  selected: string | null; openRequest?: number; onSelect: (id: string | null) => void; onChanged: () => void
}) {
  const [filter, setFilter] = useState<Filter>('PENDING')
  const [query, setQuery] = useState('')
  const [rows, setRows] = useState<SwingInstallation[]>([])
  const [page, setPage] = useState(0)
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [detail, setDetail] = useState<SwingDetail | null>(null)
  const [opening, setOpening] = useState(false)
  const [detailError, setDetailError] = useState('')
  const [review, setReview] = useState<Review | null>(null)
  const [invalid, setInvalid] = useState<Field | null>(null)
  const [formError, setFormError] = useState('')
  const [saving, setSaving] = useState(false)
  const [notice, setNotice] = useState('')
  const [payment, setPayment] = useState<Payment | null>(null)
  const [paying, setPaying] = useState(false)
  const [paymentError, setPaymentError] = useState('')
  const lastLoad = useRef(0)
  const lastOpen = useRef(0)
  const heading = useRef<HTMLHeadingElement>(null)
  const id = useId()

  const load = useCallback(async (status: Filter, text: string, nextPage = 0) => {
    const ticket = ++lastLoad.current
    setLoading(true); setError('')
    try {
      const result = await adminFetch<SwingPage>(`/admin/swing?status=${status}&q=${encodeURIComponent(text.trim())}&page=${nextPage}`)
      if (ticket !== lastLoad.current) return
      setRows((current) => nextPage === 0 ? result.items : [...current, ...result.items])
      setPage(result.page); setHasMore(result.hasMore)
    } catch (cause) {
      if (ticket === lastLoad.current) setError(describeError(cause, 'No pudimos leer los servidores de Swing.'))
    } finally {
      if (ticket === lastLoad.current) setLoading(false)
    }
  }, [adminFetch, describeError])

  useEffect(() => { void load(filter, query) }, [filter, query, load])

  const open = useCallback(async (installationId: string) => {
    const ticket = ++lastOpen.current
    setOpening(true); setDetailError(''); setNotice(''); setFormError(''); setInvalid(null)
    try {
      const found = await adminFetch<SwingDetail>(`/admin/swing/${installationId}`)
      if (ticket !== lastOpen.current) return
      setDetail(found); setReview(reviewOf(found.installation))
      requestAnimationFrame(() => heading.current?.focus())
    } catch (cause) {
      if (ticket === lastOpen.current) { setDetail(null); setDetailError(describeError(cause, 'No pudimos abrir el servidor.')) }
    } finally {
      if (ticket === lastOpen.current) setOpening(false)
    }
  }, [adminFetch, describeError])

  useEffect(() => {
    if (selected) void open(selected)
    else { lastOpen.current++; setDetail(null); setReview(null); setDetailError('') }
  }, [open, selected, openRequest])

  function placeRow(saved: SwingInstallation) {
    setRows((current) => filter === 'ALL' || filter === saved.status
      ? current.some((row) => row.id === saved.id) ? current.map((row) => row.id === saved.id ? saved : row) : current
      : current.filter((row) => row.id !== saved.id))
  }

  function edit(change: Partial<Review>) {
    setReview((current) => current && { ...current, ...change })
    setInvalid(null); setFormError('')
  }

  async function save(event: FormEvent) {
    event.preventDefault()
    if (!detail || !review) return
    setNotice('')
    const problem = reviewProblem(review)
    if (problem) {
      setInvalid(problem.field); setFormError(problem.message)
      document.getElementById(`${id}-${problem.field}`)?.focus()
      return
    }
    setSaving(true); setFormError('')
    try {
      const terminals = review.terminals.trim()
      const saved = await adminFetch<SwingInstallation>(`/admin/swing/${detail.installation.id}`, { method: 'PATCH', body: {
        status: review.status, expiresOn: review.expiresOn || null, serverPriceCents: pesosToCents(review.server),
        terminals: terminals === '' ? null : Number(terminals), terminalPriceCents: pesosToCents(review.terminalPrice),
        notes: review.notes.trim() || null, companyId: detail.installation.companyId ?? null,
        businessName: review.businessName.trim(), contactName: review.contactName.trim(), phone: review.phone.trim(), email: review.email.trim(),
      } })
      setDetail((current) => current && { ...current, installation: saved }); setReview(reviewOf(saved)); placeRow(saved); onChanged()
      setNotice(saved.status === 'VALIDATED' ? `Licencia validada. Vence el ${formatDay(saved.expiresOn)}.` : saved.status === 'REJECTED' ? 'Identificación rechazada. El programa se lo indica al negocio.' : 'Cambios guardados. El servidor sigue por validar.')
    } catch (cause) {
      setFormError(describeError(cause, 'No pudimos guardar la validación.'))
    } finally { setSaving(false) }
  }

  function startPayment() {
    if (!detail) return
    setPaymentError('')
    setPayment({ paidOn: todayIso(), reference: '', amount: centsToPesos(detail.installation.annualCents) })
  }

  async function recordPayment() {
    if (!detail || !payment) return
    const amountCents = pesosToCents(payment.amount)
    if (!payment.paidOn) { setPaymentError('Indica la fecha del pago.'); return }
    if (Number.isNaN(amountCents)) { setPaymentError('Escribe el monto en pesos, por ejemplo 4,890.00.'); return }
    setPaying(true); setPaymentError('')
    try {
      const updated = await adminFetch<SwingDetail>(`/admin/swing/${detail.installation.id}/payments`, { method: 'POST', body: { paidOn: payment.paidOn, reference: payment.reference.trim() || null, amountCents } })
      setDetail(updated); setReview(reviewOf(updated.installation)); placeRow(updated.installation); setPayment(null); onChanged()
      setNotice(`Pago registrado. La licencia vence ahora el ${formatDay(updated.installation.expiresOn)}.`)
    } catch (cause) {
      setPaymentError(describeError(cause, 'No pudimos registrar el pago.'))
    } finally { setPaying(false) }
  }

  const installation = detail?.installation
  const annual = review ? annualOf(review) : null
  const field = (name: Field) => ({ id: `${id}-${name}`, 'aria-invalid': invalid === name || undefined, 'aria-describedby': invalid === name ? `${id}-error` : undefined })

  return <section className="admin-customers admin-swing" aria-labelledby="admin-swing-heading">
    <div className="admin-section-heading"><div><h2 id="admin-swing-heading">Clientes de Swing</h2><p>Los servidores del programa de escritorio se identifican solos al actualizarse. Corrobora el negocio, valida su licencia con lo que paga al año y registra cada pago.</p></div></div>
    <div className="admin-list-controls">
      <div className="segmented" role="group" aria-label="Estado de los servidores">{(Object.keys(filterLabels) as Filter[]).map((value) => <button key={value} type="button" aria-pressed={filter === value} onClick={() => setFilter(value)}>{filterLabels[value]}</button>)}</div>
      <SearchField value={query} label="Buscar servidores de Swing" placeholder="Negocio, contacto o correo" onCommit={(value) => setQuery(value)} />
    </div>
    {error && rows.length > 0 && <Notice>{error}</Notice>}
    {loading && rows.length === 0 ? <LoadingState label="Leyendo los servidores…" />
      : error && rows.length === 0 ? <ErrorState title={error} retry={() => void load(filter, query)} busy={loading} />
      : rows.length === 0 ? <EmptyState title={query.trim() ? 'Ningún servidor coincide' : filter === 'PENDING' ? 'Nada por validar' : 'No hay servidores en esta lista'} description={query.trim() ? 'Prueba con otra parte del nombre, el contacto o el correo.' : 'Cuando un servidor de BsPOS Swing se identifique, aparecerá aquí por validar.'} />
      : <div className="price-table-wrap admin-swing-table"><table>
        <thead><tr><th><span className="sr-only">Acciones</span></th><th>Negocio</th><th>Contacto</th><th className="admin-amount">Terminales declaradas</th><th>Versión</th><th>Identificado el</th><th>Estado</th></tr></thead>
        <tbody>{rows.map((row) => <tr key={row.id} aria-current={selected === row.id ? 'true' : undefined}>
          <td><Button emphasis="outline" intent="neutral" busy={opening && selected === row.id} onClick={() => selected === row.id ? void open(row.id) : onSelect(row.id)}>Ver</Button></td>
          <td><strong>{row.businessName}</strong>{(row.city || row.state) && <span className="admin-customers-sub">{[row.city, row.state].filter(Boolean).join(', ')}</span>}</td>
          <td>{row.contactName}<span className="admin-customers-sub">{row.email}</span></td>
          <td className="admin-amount">{row.declaredTerminals}</td>
          <td>{row.programVersion ?? '—'}</td>
          <td>{when.format(new Date(row.identifiedAt))}</td>
          <td><span className="status-chip" data-tone={stateTone(row.status)}>{statusLabels[row.status] ?? row.status}</span></td>
        </tr>)}</tbody>
      </table></div>}
    {!error && hasMore && <div className="admin-customers-more"><Button emphasis="outline" intent="neutral" busy={loading} onClick={() => void load(filter, query, page + 1)}>Más servidores</Button></div>}

    {detailError && <Notice>{detailError}</Notice>}
    {opening && !detail && <LoadingState label="Abriendo el servidor…" />}
    {installation && review && <article className="admin-customer-detail" aria-labelledby={`${id}-name`}>
      <header className="admin-customer-heading">
        <div><h3 id={`${id}-name`} ref={heading} tabIndex={-1}>{installation.businessName}</h3><p className="field-hint">Se identificó el {when.format(new Date(installation.identifiedAt))}.</p></div>
        <Button emphasis="ghost" intent="neutral" onClick={() => onSelect(null)}>Cerrar</Button>
      </header>
      {notice && <Notice tone="success">{notice}</Notice>}

      <div className="admin-swing-detail">
        <section className="admin-customer-block" aria-labelledby={`${id}-facts`}>
          <h4 id={`${id}-facts`}>Identificación</h4>
          <dl className="admin-facts">
            <div><dt>Ciudad</dt><dd>{[installation.city, installation.state].filter(Boolean).join(', ') || '—'}</dd></div>
            <div><dt>Teléfono</dt><dd><a href={`tel:${installation.phone}`}>{installation.phone}</a></dd></div>
            <div><dt>Correo</dt><dd><a href={`mailto:${installation.email}`}>{installation.email}</a></dd></div>
            <div><dt>Terminales declaradas</dt><dd>{installation.declaredTerminals}</dd></div>
            <div><dt>Equipo</dt><dd>{installation.hostname ?? '—'}</dd></div>
            <div><dt>Sistema</dt><dd>{installation.os ?? '—'}</dd></div>
            <div><dt>Versión del programa</dt><dd>{installation.programVersion ?? '—'}</dd></div>
            <div><dt>Última consulta</dt><dd>{installation.lastSeenAt ? when.format(new Date(installation.lastSeenAt)) : 'Aún no consulta su estado'}</dd></div>
            {installation.companyName && <div><dt>Empresa del portal</dt><dd>{installation.companyName}</dd></div>}
          </dl>
        </section>

        <form noValidate onSubmit={save} className="admin-customer-block admin-swing-form" aria-labelledby={`${id}-review`}>
          <h4 id={`${id}-review`}>Validación</h4>
          <NativeSelect label="Estado" value={review.status} onChange={(event) => edit({ status: event.target.value as Status })}>
            {(Object.keys(statusLabels) as Status[]).map((value) => <option key={value} value={value}>{statusLabels[value]}</option>)}
          </NativeSelect>
          <div className="form-field"><label htmlFor={`${id}-expiresOn`}>Vence</label><input type="date" {...field('expiresOn')} value={review.expiresOn} onChange={(event) => edit({ expiresOn: event.target.value })} /></div>
          <div className="form-field"><label htmlFor={`${id}-server`}>Pago anual del servidor (MXN)</label><input inputMode="decimal" placeholder="0.00" {...field('server')} value={review.server} onChange={(event) => edit({ server: event.target.value })} /></div>
          <div className="form-field"><label htmlFor={`${id}-terminals`}>Terminales que paga</label><input type="number" min={0} max={1000} step={1} {...field('terminals')} value={review.terminals} onChange={(event) => edit({ terminals: event.target.value })} /><p className="field-hint">Declaró {installation.declaredTerminals}.</p></div>
          <div className="form-field"><label htmlFor={`${id}-terminalPrice`}>Pago anual por terminal (MXN)</label><input inputMode="decimal" placeholder="0.00" {...field('terminalPrice')} value={review.terminalPrice} onChange={(event) => edit({ terminalPrice: event.target.value })} /></div>
          <p className="admin-swing-total" aria-live="polite"><span>Total anual</span><strong>{annual === null ? '—' : formatMoney(annual)}</strong></p>
          <div className="form-field admin-swing-wide"><label htmlFor={`${id}-notes`}>Notas <span className="field-optional">opcional</span></label><textarea id={`${id}-notes`} rows={3} maxLength={1000} value={review.notes} onChange={(event) => edit({ notes: event.target.value })} /></div>
          <h4 className="admin-swing-wide">Contacto para los avisos</h4>
          <div className="form-field"><label htmlFor={`${id}-business`}>Negocio</label><input id={`${id}-business`} maxLength={160} value={review.businessName} onChange={(event) => edit({ businessName: event.target.value })} /></div>
          <div className="form-field"><label htmlFor={`${id}-contact`}>Contacto</label><input id={`${id}-contact`} maxLength={160} value={review.contactName} onChange={(event) => edit({ contactName: event.target.value })} /></div>
          <div className="form-field"><label htmlFor={`${id}-phone`}>Teléfono</label><input id={`${id}-phone`} type="tel" maxLength={30} value={review.phone} onChange={(event) => edit({ phone: event.target.value })} /></div>
          <div className="form-field"><label htmlFor={`${id}-email`}>Correo</label><input type="email" maxLength={255} {...field('email')} value={review.email} onChange={(event) => edit({ email: event.target.value })} /></div>
          {formError && <div className="admin-swing-wide" id={`${id}-error`}><Notice>{formError}</Notice></div>}
          <div className="admin-swing-actions admin-swing-wide">
            <Button type="submit" busy={saving}>Guardar validación</Button>
            {installation.status === 'VALIDATED' && <Button emphasis="outline" disabled={saving} onClick={startPayment}>Registrar pago</Button>}
          </div>
        </form>
      </div>

      <section className="admin-customer-block" aria-labelledby={`${id}-payments`}>
        <h4 id={`${id}-payments`}>Pagos</h4>
        {detail.payments.length === 0 ? <p className="field-hint">Sin pagos registrados.</p> : <div className="price-table-wrap admin-swing-payments"><table>
          <thead><tr><th>Pagado el</th><th>Referencia</th><th>Vencía</th><th>Vence ahora</th><th>Registrado</th><th className="admin-amount">Monto</th></tr></thead>
          <tbody>{detail.payments.map((item) => <tr key={item.id}><td>{formatDay(item.paidOn)}</td><td>{item.reference ?? '—'}</td><td>{formatDay(item.previousExpiresOn)}</td><td>{formatDay(item.newExpiresOn)}</td><td>{when.format(new Date(item.recordedAt))}</td><td className="admin-amount">{formatMoney(item.amountCents)}</td></tr>)}</tbody>
        </table></div>}
      </section>
    </article>}

    <ConfirmDialog open={payment !== null} title="Registrar el pago del año" action="Registrar pago" intent="primary" busy={paying} error={paymentError || undefined}
      onCancel={() => { if (!paying) setPayment(null) }} onConfirm={() => void recordPayment()}>
      {payment && installation && <div className="admin-swing-payment">
        <p>La licencia de <strong>{installation.businessName}</strong> pasa de vencer el {formatDay(installation.expiresOn)} a un año después.</p>
        <div className="form-field"><label htmlFor={`${id}-paidOn`}>Fecha de pago</label><input id={`${id}-paidOn`} type="date" value={payment.paidOn} onChange={(event) => setPayment({ ...payment, paidOn: event.target.value })} /></div>
        <div className="form-field"><label htmlFor={`${id}-reference`}>Referencia <span className="field-optional">opcional</span></label><input id={`${id}-reference`} maxLength={200} value={payment.reference} onChange={(event) => setPayment({ ...payment, reference: event.target.value })} /></div>
        <div className="form-field"><label htmlFor={`${id}-amount`}>Monto (MXN)</label><input id={`${id}-amount`} inputMode="decimal" value={payment.amount} onChange={(event) => setPayment({ ...payment, amount: event.target.value })} /><p className="field-hint">Vacío registra el total anual de la licencia.</p></div>
      </div>}
    </ConfirmDialog>
  </section>
}

/** The payment details the renewal e-mails and the program's notice carry. */
export function SwingPaymentInstructions({ adminFetch, describeError }: { adminFetch: AdminFetch; describeError: (cause: unknown, fallback: string) => string }) {
  const [text, setText] = useState('')
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const id = useId()

  const load = useCallback(async () => {
    setLoading(true); setLoadError('')
    try { setText((await adminFetch<{ paymentInstructions?: string | null }>('/admin/swing/settings')).paymentInstructions ?? '') }
    catch (cause) { setLoadError(describeError(cause, 'No pudimos leer los datos de pago.')) }
    finally { setLoading(false) }
  }, [adminFetch, describeError])

  useEffect(() => { void load() }, [load])

  async function save(event: FormEvent) {
    event.preventDefault()
    setSaving(true); setError(''); setNotice('')
    try {
      const saved = await adminFetch<{ paymentInstructions?: string | null }>('/admin/swing/settings', { method: 'PUT', body: { paymentInstructions: text } })
      setText(saved.paymentInstructions ?? ''); setNotice('Datos de pago guardados. Los próximos avisos ya los llevan.')
    } catch (cause) { setError(describeError(cause, 'No pudimos guardar los datos de pago.')) } finally { setSaving(false) }
  }

  return <section className="admin-customers admin-swing-settings" aria-labelledby={`${id}-heading`}>
    <div className="admin-section-heading"><div><h2 id={`${id}-heading`}>Datos de pago para los avisos</h2><p>Los correos de renovación y el aviso que muestra el programa Swing llevan este texto: banco, CLABE, a nombre de quién y a dónde enviar el comprobante.</p></div></div>
    {loading ? <LoadingState label="Leyendo los datos de pago…" />
      : loadError ? <ErrorState title={loadError} retry={() => void load()} />
      : <form noValidate onSubmit={save} className="admin-customer-block admin-swing-instructions">
        <div className="form-field"><label htmlFor={`${id}-text`}>Datos de pago</label><textarea id={`${id}-text`} rows={5} maxLength={2000} value={text} aria-describedby={`${id}-count`} onChange={(event) => { setText(event.target.value); setNotice('') }} /><p id={`${id}-count`} className="field-hint">{text.length.toLocaleString('es-MX')} de 2,000 caracteres.</p></div>
        {notice && <Notice tone="success">{notice}</Notice>}
        {error && <Notice>{error}</Notice>}
        <div><Button type="submit" busy={saving}>Guardar datos de pago</Button></div>
      </form>}
  </section>
}
